import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check } from "lucide-react";

const QUOTE_LABEL = { ask: "Usually ask for a quote", start: "Usually just start the work", depends: "Depends" };
const SECTOR_LABEL = { residential: "Residential", commercial: "Commercial", both: "Both" };
const PREF_LABEL = { preferred: "Preferred", backup: "Backup", "": "Neither" };

const EMPTY = {
  disciplines: [], company: "", contacts: "", emails: "", phone: "",
  typical_fee: "", typical_turnaround: "", quote_needed: "depends",
  preferred: "", sector: "residential", notes: "",
};

export default function ConsultantDialog({ open, onOpenChange, disciplines, consultant, onSaved }) {
  const isEdit = !!consultant;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(consultant ? {
        disciplines: consultant.disciplines || [], company: consultant.company || "",
        contacts: consultant.contacts || "", emails: (consultant.emails || []).join("; "),
        phone: consultant.phone || "", typical_fee: consultant.typical_fee || "",
        typical_turnaround: consultant.typical_turnaround || "",
        quote_needed: consultant.quote_needed || "depends", preferred: consultant.preferred || "",
        sector: consultant.sector || "residential", notes: consultant.notes || "",
      } : EMPTY);
    }
  }, [open, consultant]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleDisc = (d) => set({
    disciplines: form.disciplines.includes(d) ? form.disciplines.filter((x) => x !== d) : [...form.disciplines, d],
  });

  const save = async () => {
    if (!form.company.trim()) { toast.error("Company is required"); return; }
    setSaving(true);
    const payload = { ...form, emails: form.emails.split(";").map((e) => e.trim()).filter(Boolean) };
    try {
      if (isEdit) await api.patch(`/consultants/${consultant.consultant_id}`, payload);
      else await api.post("/consultants", payload);
      toast.success(isEdit ? "Consultant updated" : "Consultant added");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(apiErr(e, "Could not save consultant")); }
    finally { setSaving(false); }
  };

  const L = "text-xs font-semibold uppercase tracking-wider text-zinc-500";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg w-[calc(100vw-2rem)] sm:w-full max-h-[90vh] overflow-y-auto" data-testid="consultant-dialog">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Edit consultant" : "Add consultant"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <label className={L}>Discipline(s)</label>
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="discipline-picker">
              {disciplines.map((d) => {
                const on = form.disciplines.includes(d);
                return (
                  <button key={d} type="button" onClick={() => toggleDisc(d)}
                    className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${on ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>
                    {d}{on && <Check className="inline h-3 w-3 ml-1" />}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className={L}>Company</label>
            <Input data-testid="consultant-company" value={form.company} className="mt-1.5 rounded-sm"
              onChange={(e) => set({ company: e.target.value })} placeholder="e.g. Control Line Consulting" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className={L}>Contact person(s)</label>
              <Input value={form.contacts} className="mt-1.5 rounded-sm" onChange={(e) => set({ contacts: e.target.value })} />
            </div>
            <div>
              <label className={L}>Phone</label>
              <Input value={form.phone} className="mt-1.5 rounded-sm" onChange={(e) => set({ phone: e.target.value })} />
            </div>
          </div>
          <div>
            <label className={L}>Email(s) — separate with ;</label>
            <Input data-testid="consultant-emails" value={form.emails} className="mt-1.5 rounded-sm"
              onChange={(e) => set({ emails: e.target.value })} placeholder="one@x.com; two@x.com" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className={L}>Typical fee</label>
              <Input value={form.typical_fee} className="mt-1.5 rounded-sm" onChange={(e) => set({ typical_fee: e.target.value })} placeholder="$700 + GST" />
            </div>
            <div>
              <label className={L}>Typical turnaround</label>
              <Input value={form.typical_turnaround} className="mt-1.5 rounded-sm" onChange={(e) => set({ typical_turnaround: e.target.value })} placeholder="2–3 weeks" />
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className={L}>Quote needed?</label>
              <Select value={form.quote_needed} onValueChange={(v) => set({ quote_needed: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="consultant-quote-needed"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(QUOTE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className={L}>Preferred</label>
              <Select value={form.preferred || "none"} onValueChange={(v) => set({ preferred: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="consultant-preferred"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="preferred">Preferred ⭐</SelectItem>
                  <SelectItem value="backup">Backup</SelectItem>
                  <SelectItem value="none">Neither</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={L}>Sector</label>
              <Select value={form.sector} onValueChange={(v) => set({ sector: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="consultant-sector"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(SECTOR_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className={L}>Notes</label>
            <Textarea value={form.notes} className="mt-1.5 rounded-sm" rows={2} onChange={(e) => set({ notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="save-consultant-button" onClick={save} disabled={saving} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add consultant"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { QUOTE_LABEL, SECTOR_LABEL, PREF_LABEL };
